<template>
  <div>
    <template v-if="!isLoginPage">
      <div class="app-layout" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <AppSidebar @collapse-change="sidebarCollapsed = $event" />
        <div class="main-wrap">
          <header class="main-header zone-header">
            <div class="header-left">
              <h1 class="header-title">股东智能决策系统</h1>
              <div class="header-breadcrumb">
                <span>{{ breadcrumb.parent || '首页' }}</span>
                <span> / </span>
                <span class="current">{{ breadcrumb.title }}</span>
              </div>
            </div>
            <div class="header-right">
              <div class="user-block">
                <span class="user-name">{{ currentUserLabel }}</span>
                <span v-if="currentUserRoleLabel" class="user-role">{{ currentUserRoleLabel }}</span>
              </div>
              <div class="header-actions">
                <a class="header-link" href="javascript:;" @click="changePwdVisible = true">修改密码</a>
                <a class="header-link danger" href="javascript:;" @click="handleLogout">退出登录</a>
              </div>
            </div>
          </header>

          <el-dialog
            v-model="changePwdVisible"
            title="修改密码"
            width="420px"
          >
            <el-form :model="changePwdForm" label-width="80px">
              <el-form-item label="旧密码">
                <el-input v-model="changePwdForm.oldPassword" type="password" show-password />
              </el-form-item>
              <el-form-item label="新密码">
                <el-input v-model="changePwdForm.newPassword" type="password" show-password />
              </el-form-item>
              <el-form-item label="确认新密码">
                <el-input v-model="changePwdForm.confirmPassword" type="password" show-password />
              </el-form-item>
            </el-form>
            <template #footer>
              <span class="dialog-footer">
                <el-button @click="changePwdVisible = false">取 消</el-button>
                <el-button type="primary" @click="submitChangePassword">确 定</el-button>
              </span>
            </template>
          </el-dialog>

          <main class="zone-content" style="padding: 20px;">
            <router-view />
          </main>
        </div>
      </div>
    </template>
    <template v-else>
      <router-view />
    </template>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { computed, ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import AppSidebar from './components/AppSidebar.vue'
import '@/styles/zone-content.css'
import '@/styles/zone-mobile.css'

const route = useRoute()
const router = useRouter()
const sidebarCollapsed = ref(false)
const breadcrumb = computed(() => {
  const meta = route.meta || {}
  return {
    parent: meta.parent || meta.title || '',
    title: meta.title || '首页'
  }
})

const isLoginPage = computed(() => route.path === '/login')

const currentUser = ref(null)
const changePwdVisible = ref(false)
const changePwdForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const currentUserLabel = computed(() => {
  const u = currentUser.value
  return u && u.username ? u.username : '未登录'
})

const currentUserRoleLabel = computed(() => {
  const u = currentUser.value
  if (!u || !u.role) return ''
  if (u.role === 'admin') return '管理员'
  if (u.role === 'chairman') return '领导'
  if (u.role === 'secretary') return '助理'
  return u.role
})

const handleLogout = async () => {
  try {
    await axios.post('/api/auth/logout/')
  } catch (e) {
    // ignore
  }
  localStorage.removeItem('currentUser')
  router.push('/login')
}

const refreshCurrentUser = async () => {
  const raw = localStorage.getItem('currentUser')
  if (raw) {
    try {
      currentUser.value = JSON.parse(raw)
    } catch (e) {
      currentUser.value = null
    }
  }
  try {
    const res = await axios.get('/api/auth/me/')
    if (res.data.status === 'ok') {
      const user = res.data.data || {}
      currentUser.value = user
      localStorage.setItem('currentUser', JSON.stringify(user))
    }
  } catch (e) {
    // 未登录或接口异常时保持现状
  }
}

const submitChangePassword = async () => {
  if (!changePwdForm.value.oldPassword || !changePwdForm.value.newPassword) {
    ElMessage.warning('请输入旧密码和新密码')
    return
  }
  if (changePwdForm.value.newPassword !== changePwdForm.value.confirmPassword) {
    ElMessage.error('两次输入的新密码不一致')
    return
  }
  try {
    const res = await axios.post('/api/auth/change_password/', {
      oldPassword: changePwdForm.value.oldPassword,
      newPassword: changePwdForm.value.newPassword
    })
    if (res.data.status === 'ok') {
      ElMessage.success('密码修改成功，请重新登录')
      changePwdVisible.value = false
      changePwdForm.value.oldPassword = ''
      changePwdForm.value.newPassword = ''
      changePwdForm.value.confirmPassword = ''
      localStorage.removeItem('currentUser')
      router.push('/login')
    } else {
      ElMessage.error(res.data.message || '密码修改失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '密码修改失败')
  }
}

onMounted(() => {
  refreshCurrentUser()
})

watch(() => route.path, (newPath, oldPath) => {
  if (newPath !== '/login' && oldPath === '/login') {
    refreshCurrentUser()
  }
})
</script>

<style scoped>
.app-layout {
  display: flex;
  height: 100vh;
  background: #f5f7fa;
}
.app-layout .main-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 240px;
  background: #f5f7fa;
  overflow-y: auto;
  overflow-x: hidden;
}
.app-layout.sidebar-collapsed .main-wrap {
  margin-left: 64px;
}
</style>
